import React, { useState, useEffect } from 'react';
import { CouponService, validateCouponCodeFormat } from '../services/couponService';
// Firestore imports removed
import { ApiService } from '../services/api_service';

const CouponInput = ({
  onCouponApplied,
  onCouponRemoved,
  appliedCoupon,
  subtotal,
  userId,
  eventId,
  disabled = false
}) => {
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [isLoadingCoupons, setIsLoadingCoupons] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  // Fetch available coupons when component mounts or when expanded
  useEffect(() => {
    if (isExpanded && availableCoupons.length === 0) {
      fetchAvailableCoupons();
    }
  }, [isExpanded]);

  const fetchAvailableCoupons = async () => {
    setIsLoadingCoupons(true);
    setError('');

    try {
      const response = await ApiService.getCoupons();
      let allCoupons = response.data || response;

      if (allCoupons && !Array.isArray(allCoupons) && typeof allCoupons === 'object') {
        allCoupons = Object.values(allCoupons);
      }
      if (!Array.isArray(allCoupons)) allCoupons = [];

      const coupons = [];

      allCoupons.forEach((couponData) => {
        // Filter active coupons
        if (couponData.isActive !== true) return;

        const now = new Date();
        // Handle string date or Firestore Timestamp from API (if migrated literally)
        // Usually API returns ISO strings.
        const expiresAt = couponData.expiresAt?.toDate ? couponData.expiresAt.toDate() : new Date(couponData.expiresAt);

        // Check if coupon is not expired and has usage left
        if (now <= expiresAt && couponData.usedCount < couponData.maxUsage) {
          // Check if coupon meets minimum purchase requirement
          if (subtotal >= couponData.minPurchaseAmount) {
            // Check if coupon is applicable to this event (if specified)
            if (!couponData.applicableEventIds || couponData.applicableEventIds.includes(eventId)) {
              // Check if coupon is assigned to this user (if specified)
              if (!couponData.assignedToUserIds || couponData.assignedToUserIds.includes(userId)) {
                coupons.push(couponData);
              }
            }
          }
        }
      });

      setAvailableCoupons(coupons);
    } catch (error) {
      console.error('Error fetching coupons:', error);
      setError('Failed to load available coupons');
    } finally {
      setIsLoadingCoupons(false);
    }
  };

  const handleApplyCoupon = async (coupon) => {
    setIsLoading(true);
    setError('');

    try {
      const result = await CouponService.validateCoupon(
        coupon.code,
        subtotal,
        userId,
        eventId
      );

      if (result.success) {
        onCouponApplied(result.data);
        setIsExpanded(false);
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Failed to validate coupon. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    onCouponRemoved();
    setError('');
  };

  const toggleExpanded = () => {
    if (!disabled) {
      setIsExpanded(!isExpanded);
      setError('');
    }
  };

  const formatCouponDisplay = (coupon) => {
    if (coupon.discountType === 'percentage') {
      return `${coupon.discountValue}% OFF`;
    } else {
      return `₹${coupon.discountValue} OFF`;
    }
  };

  const calculateDiscountAmount = (coupon) => {
    let discountAmount = 0;
    if (coupon.discountType === 'percentage') {
      discountAmount = (subtotal * coupon.discountValue) / 100;
      if (discountAmount > coupon.maxDiscountAmount) {
        discountAmount = coupon.maxDiscountAmount;
      }
    } else if (coupon.discountType === 'fixedAmount') {
      discountAmount = coupon.discountValue;
    }
    return Math.min(discountAmount, subtotal);
  };

  return (
    <div className="coupon-section">
      {/* Coupon Toggle Button */}
      <div
        className={`apply-coupon-section ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        onClick={toggleExpanded}
      >
        <span className="apply-coupon-label">
          {appliedCoupon ? 'Applied Coupon' : 'Apply Coupon'}
        </span>
        <div className={`coupon-arrow ${isExpanded ? 'rotate' : ''}`}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="transition-transform duration-200"
          >
            <polyline points="6,9 12,15 18,9"></polyline>
          </svg>
        </div>
      </div>

      {/* Applied Coupon Display */}
      {appliedCoupon && (
        <div className="applied-coupon-display">
          <div className="applied-coupon-info">
            <div className="coupon-code-display">
              <span className="coupon-code">{appliedCoupon.couponCode}</span>
              <span className="coupon-discount">
                {CouponService.formatCouponDisplay(appliedCoupon)}
              </span>
            </div>
            <button
              onClick={handleRemoveCoupon}
              className="remove-coupon-btn"
              disabled={disabled}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <div className="coupon-savings">
            You saved ₹{appliedCoupon.discountAmount.toFixed(2)}
          </div>
        </div>
      )}

      {/* Available Coupons List */}
      {isExpanded && !appliedCoupon && (
        <div className="coupon-selection-form">
          {isLoadingCoupons ? (
            <div className="coupon-loading">
              <div className="loading-spinner">
                <div className="spinner"></div>
              </div>
              <span>Loading available coupons...</span>
            </div>
          ) : availableCoupons.length > 0 ? (
            <div className="available-coupons-list">
              <h4 className="coupon-list-title">Available Coupons</h4>
              {availableCoupons.map((coupon) => (
                <div key={coupon.id} className="coupon-item">
                  <div className="coupon-info">
                    <div className="coupon-header">
                      <span className="coupon-code-badge">{coupon.code}</span>
                      <span className="coupon-discount-text">
                        {formatCouponDisplay(coupon)}
                      </span>
                    </div>
                    <div className="coupon-details">
                      <span className="coupon-savings-text">
                        Save ₹{calculateDiscountAmount(coupon).toFixed(2)}
                      </span>
                      {coupon.minPurchaseAmount > 0 && (
                        <span className="coupon-min-amount">
                          Min. purchase: ₹{coupon.minPurchaseAmount}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleApplyCoupon(coupon)}
                    disabled={disabled || isLoading}
                    className="apply-coupon-item-btn"
                  >
                    {isLoading ? (
                      <div className="loading-spinner">
                        <div className="spinner"></div>
                      </div>
                    ) : (
                      'Apply'
                    )}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-coupons-available">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              <span>No coupons available for this purchase</span>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="coupon-error">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
              <span>{error}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CouponInput;